import * as React from 'react'
import ReactSelect from 'react-select'
import styled, { AnyStyledComponent } from 'styled-components'
import I18n from '../../../../core/i18n'
import { agentService, utilService } from '../../../../core/services'
import { BREAKPOINT_TABLET_SMALL } from '../../../../static/constants'
import * as constants from '../../../../static/constants'
import * as interfaces from '../../../../static/interfaces'
import { InputText, InputTextArea, Button, Panel, InputLabel } from '../../../atoms'
import { Form } from '../../../molecules'
import SidebarLayout from '../../_layouts/sidebarLayout'
import SvgIcon from 'components/atoms/SvgIcon'
import { IJsonResponse } from 'core/JsonApiSerializer'
import { SoundoutAndFormalRequestVariablesDescription } from 'components/atoms/SoundoutAndFormalRequestVariablesDescription'
import { GuideKindSelector } from 'components/molecules/GuideKindSelector'
import { useAgentPostSelection } from 'utils/soundoutOrFormalRequest'

declare var window: interfaces.IWindow

const FIELDS = {
  title: 'title',
  mail_subject: 'mail_subject',
  mail_text: 'mail_text',
  agent_id: 'agent_id',
}
const title = I18n.t('agent.create_new', {
  model: I18n.t('formal_request_template', { scope: 'activerecord.models' }),
})

interface IProps {
  posts: IJsonResponse
  agent_ranks: IJsonResponse
  user: any
}

const AgentFormalRequestTemplateNew: React.FC<IProps> = props => {
  const { data: posts } = agentService.getDataFromJson(props.posts)
  const { data: agentRanks } = utilService.getDataFromJson(props.agent_ranks)
  const { data: userData } = utilService.getDataFromJson(props.user)

  const { postOptions, selectedPosts, setSelectedPosts } = useAgentPostSelection({ posts })

  const [agentRankId, setAgentRankId] = React.useState<number | null>(null)
  const specialRankOptions = agentRanks
    .filter(rank => rank.enabled)
    .map(rank => ({ value: String(rank.id), label: rank.name }))

  const handleSubmit = async (initialValues, values) => {
    const post_ids = selectedPosts.map(post => Number(post.value))
    if (post_ids.length === 0) {
      window.flashMessages.addMessage({
        text: 'postが選択されていないか、postの選択に不備があります。',
        type: 'error',
      })
      return
    }
    const { createdTemplate, flush } = await agentService.createFormalRequestTemplate({
      ...values,
      post_ids,
      agent_rank_id: agentRankId,
    })
    window.flashMessages.addMessage({ text: flush.message, type: flush.type })
    location.href = '/agent/formal_request_templates'
  }

  return (
    <SidebarLayout user={userData}>
      <Wrapper>
        <Panel title={title}>
          <Form fields={FIELDS} handleSubmit={handleSubmit}>
            <input
              name="agent_id"
              tabIndex={-1}
              autoComplete="off"
              style={{ opacity: 0, height: 0 }}
              value={userData.id || ''}
              required={true}
              onChange={() => {
                return
              }}
            />
            <S.FormItemWrapper>
              <S.GuideKindWrapper>
                <InputLabel label={I18n.t('guide_kind.label')} />
                <GuideKindSelector
                  name="guide_kind"
                  specialRankOptions={specialRankOptions}
                  agentRankId={agentRankId}
                  onChange={setAgentRankId}
                />
              </S.GuideKindWrapper>
            </S.FormItemWrapper>
            <S.FormItemWrapper>
              <InputText
                required={true}
                name="title"
                defaultValue=""
                label={I18n.t('generic.name')}
              />
            </S.FormItemWrapper>
            <S.FormItemWrapper>
              <InputText
                required={true}
                defaultValue=""
                name="mail_subject"
                label={I18n.t('mail_template.subject')}
              />
            </S.FormItemWrapper>
            <S.FormItemWrapper>
              <InputTextArea
                required={true}
                name="mail_text"
                label={I18n.t('generic.text')}
                defaultValue=""
              />
              <SoundoutAndFormalRequestVariablesDescription />
            </S.FormItemWrapper>
            <S.FormItemWrapper>
              <S.ReactSelectWrapper>
                <p className="ReactSelect_Title">
                  {I18n.t('generic.tour')}
                  <span className="InputText_Required">{I18n.t('generic.required')}</span>
                </p>
                <div className="ReactSelect_Main">
                  <ReactSelect
                    name="post_ids"
                    placeholder={I18n.t('generic.select_tour')}
                    options={postOptions}
                    isMulti={true}
                    onChange={setSelectedPosts}
                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                    value={selectedPosts}
                    menuPortalTarget={typeof window !== 'undefined' ? document.body : undefined}
                  />
                </div>
              </S.ReactSelectWrapper>
            </S.FormItemWrapper>
            <div className="Buttons">
              <Button>
                <a href="/agent/formal_request_templates">
                  <SvgIcon
                    name="keyboard_arrow_left"
                    className="icon"
                    ariaHidden={true}
                    role="presentation"
                  />
                  {I18n.t('generic.back_to_index')}
                </a>
              </Button>
              <Button primary={true}>{I18n.t('generic.create')}</Button>
            </div>
          </Form>
        </Panel>
      </Wrapper>
    </SidebarLayout>
  )
}

const S: { [key: string]: AnyStyledComponent } = {}

S.FormItemWrapper = styled.div`
  margin-top: 24px;
  .note {
    margin-left: 240px;
    font-size: 14px;
    margin-top: 5px;
    @media (max-width: ${BREAKPOINT_TABLET_SMALL}px) {
      margin-left: 0px;
    }
  }
`
S.GuideKindWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`
S.ReactSelectWrapper = styled.div`
  display: flex;
  margin-top: 24px;
  @media (max-width: ${BREAKPOINT_TABLET_SMALL}px) {
    flex-direction: column;
  }
  .ReactSelect_Title {
    min-width: 240px;
    font-size: 15px;
    font-weight: bold;
    line-height: 1;
    margin-top: 8px;

    .InputText_Required {
      display: inline-block;
      border-radius: 3px;
      color: ${constants.COLORS.white};
      background-color: ${constants.COLORS.caution};
      vertical-align: middle;
      font-size: 11px;
      font-weight: normal;
      padding: 4px 6px;
      margin-left: 8px;
    }

    @media (max-width: ${BREAKPOINT_TABLET_SMALL}px) {
      width: 100%;
      margin-bottom: 12px;
    }
  }
  .ReactSelect_Main {
    flex: 1;
    font-size: 15px;
  }
`

const Wrapper = styled.div`
  width: 100%;
  padding: 22px 40px 80px 46px;

  .Buttons {
    position: relative;
    display: flex;
    justify-content: center;
    margin-top: 48px;
    @media (max-width: ${constants.BREAKPOINT_TABLET_LARGE}px) {
      justify-content: flex-end;
    }

    .Button[type='submit'] {
      width: 160px;
    }

    .Button[type='button'] {
      position: absolute;
      left: 0;
      top: 0;
      display: flex;
      align-items: center;

      .icon {
        margin-left: -10px;
      }
    }
  }

  .Panel {
    max-width: none;
  }
`

export default AgentFormalRequestTemplateNew
